import { formatters, useTranslation } from "@equiem/web-ng-lib";
import { Site } from "@equiem/web-ng-lib";
import { Tag } from "@equiem/react-end-user-ui";
import { BsCheckCircle } from "@equiem/react-end-user-ui/icons";
import React, { useContext } from "react";
import { OccurrenceSummaryFragmentFragment } from "../../generated/gateway-client";
import { DateTime } from "../../lib/DateTime";
import { Card } from "../Card";
import { CardImage } from "../CardImage";
import { Clock } from "../icons/Clock";
import { Location } from "../icons/Location";
import { InteractionCounts } from "../InteractionCounts";
import { EventTag } from "./EventTag";
import { useRsvpTagProps } from "./useRsvpTagProps";

type Occurrence = OccurrenceSummaryFragmentFragment;

interface Props {
  className?: string;
  occurrence: Occurrence;
  focusable?: boolean;
  metric?: {
    ns: string;
    nx: number;
  };
}

export const EventCard: React.FC<Props> = ({ className = "", occurrence, focusable, metric }) => {
  const { i18n } = useTranslation();
  const { timezone } = useContext(Site);
  const startDate = DateTime.fromMillis(occurrence.startDate, { zone: timezone });
  const endDate = DateTime.fromMillis(occurrence.endDate, { zone: timezone });
  const metricExt = metric == null ? "" : `?ns=${metric.ns}&nx=${metric.nx}`;
  const rsvpTag = useRsvpTagProps(occurrence);

  return (
    <div className={`${className} event-card position-relative h-100`}>
      <Card
        img={<CardImage url={occurrence.eventPost?.featuredImage.url} />}
        href="/event/[uuid]"
        as={`/event/${occurrence.uuid}${metricExt}`}
        title={occurrence.title}
        focusable={focusable}
      >
        <div className="info">
          <div className="time d-flex">
            <span><Clock /></span>
            <span className="pl-1 icon-label">
              {startDate.hasSame(endDate, "day")
                ? `${formatters.datelong(startDate.valueOf(), i18n.language, { timezone })} ${formatters.timeshort(startDate.valueOf(), i18n.language, { timezone })} - ${formatters.timeshort(endDate.valueOf(), i18n.language, { timezone })}`
                : `${formatters.datelong(startDate.valueOf(), i18n.language, { timezone })} ${formatters.timeshort(startDate.valueOf(), i18n.language, { timezone })} - ${formatters.datelong(endDate.valueOf(), i18n.language, { timezone })} ${formatters.timeshort(endDate.valueOf(), i18n.language, { timezone })}`}</span>
          </div>
          {occurrence.location == null ? null : (
            <div className="location d-flex">
              <span><Location /></span>
              <span className="pl-1 icon-label">{occurrence.location}</span>
            </div>
          )}
        </div>
        <div className="counts pt-3">
          <InteractionCounts content={occurrence} />
        </div>
      </Card>
      <EventTag top="1px" left="1px" />
      {rsvpTag != null ? (
        <div className="rsvp-tag">
          <Tag variant={rsvpTag.variant} size="large" emphasis="high" border>
            {rsvpTag.showSpotsOpenIcon === true ? <span className="spots-open-icon" aria-hidden="true" /> : null}
            {rsvpTag.showCheckIcon === true ? <BsCheckCircle size={14} className="mr-1" /> : null}
            {rsvpTag.label}
          </Tag>
        </div>
      ) : null}
      <style jsx>{`
        .counts {
          margin-top: auto;
        }
        .rsvp-tag {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 1;
        }
        .spots-open-icon {
          width: 12px;
          height: 12px;
          border: 1.5px solid currentColor;
          border-radius: 50%;
          display: inline-block;
          margin-right: 0.4rem;
          vertical-align: middle;
          position: relative;
        }
        .spots-open-icon::after {
          content: "";
          position: absolute;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: currentColor;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      `}</style>
    </div>
  );
}
